/**
 * Referral Sources Settings Page
 * Marketing-Quellen verwalten (Wie haben Sie von uns erfahren?)
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, Plus, Save, Trash2, GripVertical, 
  Megaphone, Settings, Check, X, Edit2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ReferralSourcesSettingsPage = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sources, setSources] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [newSource, setNewSource] = useState({
    name: '',
    icon: '📣',
    active: true
  });

  const headers = {
    Authorization: `Bearer ${localStorage.getItem('token')}`
  };

  useEffect(() => {
    loadSources();
  }, []);

  const loadSources = async () => {
    try {
      const response = await axios.get(`${API}/referral-sources?include_inactive=true`, { headers });
      setSources(response.data || []);
    } catch (error) {
      console.error('Error loading sources:', error);
      toast.error('Fehler beim Laden der Marketing-Quellen');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSource = async () => {
    if (!newSource.name.trim()) {
      toast.error('Bitte geben Sie einen Namen ein');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/referral-sources`, newSource, { headers });
      toast.success('Marketing-Quelle hinzugefügt');
      setShowAddDialog(false);
      setNewSource({ name: '', icon: '📣', active: true });
      loadSources();
    } catch (error) {
      console.error('Error adding source:', error);
      toast.error('Fehler beim Hinzufügen');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateSource = async () => {
    if (!editingSource) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/referral-sources/${editingSource.id}`, {
        name: editingSource.name,
        icon: editingSource.icon,
        active: editingSource.active
      }, { headers });
      toast.success('Marketing-Quelle aktualisiert');
      setEditingSource(null);
      loadSources();
    } catch (error) {
      console.error('Error updating source:', error);
      toast.error('Fehler beim Aktualisieren');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSource = async (sourceId) => {
    if (!window.confirm('Möchten Sie diese Marketing-Quelle wirklich löschen?')) return;
    
    try {
      const response = await axios.delete(`${API}/referral-sources/${sourceId}`, { headers });
      if (response.data.deactivated) {
        toast.info(response.data.message);
      } else {
        toast.success('Marketing-Quelle gelöscht');
      }
      loadSources();
    } catch (error) {
      console.error('Error deleting source:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const handleToggleActive = async (source) => {
    try {
      await axios.put(`${API}/referral-sources/${source.id}`, {
        active: !source.active
      }, { headers });
      loadSources();
    } catch (error) {
      console.error('Error toggling source:', error);
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const handleInitDefaults = async () => {
    try {
      const response = await axios.post(`${API}/referral-sources/init-defaults`, {}, { headers });
      if (response.data.success) {
        toast.success(`${response.data.created} Standard-Quellen erstellt`);
        loadSources();
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      console.error('Error initializing defaults:', error);
      toast.error('Fehler beim Initialisieren');
    }
  };

  const commonEmojis = ['📣', '👥', '🔍', '📸', '📘', '📋', '📰', '🌐', '🏥', '💬', '📺', '🎯', '📧', '📱', '🎪'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl p-6">
      {/* Header */}
      <div className="mb-6">
        {!embedded && (
        <Button 
          variant="ghost" 
          onClick={() => navigate('/settings')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zurück zu Einstellungen
        </Button>
        )}
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Megaphone className="h-6 w-6 text-teal-600" />
              Marketing-Quellen
            </h1>
            <p className="text-gray-600 mt-1">
              Verwalten Sie die Optionen für "Wie haben Sie von uns erfahren?"
            </p>
          </div>
          <Button onClick={() => setShowAddDialog(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Neue Quelle
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <Card className="mb-6 border-teal-200 bg-teal-50">
        <CardContent className="pt-4">
          <p className="text-sm text-teal-800">
            <strong>💡 Tipp:</strong> Diese Optionen werden bei der Kundenregistrierung als optionales Dropdown angezeigt. 
            So erfahren Sie, über welche Kanäle neue Kunden auf Sie aufmerksam werden.
          </p>
        </CardContent>
      </Card>

      {/* Sources List */}
      {sources.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Megaphone className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Keine Marketing-Quellen vorhanden</h3>
            <p className="text-gray-600 mb-4">
              Erstellen Sie Ihre erste Marketing-Quelle oder laden Sie die Standardoptionen.
            </p>
            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={handleInitDefaults}>
                Standard-Quellen laden
              </Button>
              <Button onClick={() => setShowAddDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Neue Quelle erstellen
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Verfügbare Quellen ({sources.length})</CardTitle>
            <CardDescription>
              Ziehen Sie die Einträge, um die Reihenfolge zu ändern
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {sources.map((source, index) => (
                <div 
                  key={source.id}
                  className={`flex items-center gap-4 p-4 rounded-lg border ${
                    source.active ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-200 opacity-60'
                  }`}
                >
                  <GripVertical className="w-5 h-5 text-gray-400 cursor-grab" />
                  
                  <div className="text-2xl">{source.icon || '📣'}</div>
                  
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{source.name}</p>
                    {!source.active && (
                      <p className="text-xs text-gray-500">Deaktiviert</p>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={source.active}
                      onCheckedChange={() => handleToggleActive(source)}
                    />
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setEditingSource(source)}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleDeleteSource(source.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Neue Marketing-Quelle</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input 
                placeholder="z.B. Instagram, Empfehlung, Google..."
                value={newSource.name}
                onChange={(e) => setNewSource({...newSource, name: e.target.value})}
              />
            </div>
            <div>
              <Label>Icon</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {commonEmojis.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() => setNewSource({...newSource, icon: emoji})}
                    className={`w-10 h-10 text-xl rounded-lg border-2 transition-all ${
                      newSource.icon === emoji 
                        ? 'border-teal-500 bg-teal-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                checked={newSource.active}
                onCheckedChange={(checked) => setNewSource({...newSource, active: checked})}
              />
              <Label>Aktiv (wird bei Registrierung angezeigt)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleAddSource} disabled={saving}>
              {saving ? 'Speichert...' : 'Hinzufügen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editingSource} onOpenChange={() => setEditingSource(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Marketing-Quelle bearbeiten</DialogTitle>
          </DialogHeader>
          {editingSource && (
            <div className="space-y-4">
              <div>
                <Label>Name *</Label>
                <Input 
                  value={editingSource.name}
                  onChange={(e) => setEditingSource({...editingSource, name: e.target.value})}
                />
              </div>
              <div>
                <Label>Icon</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {commonEmojis.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      onClick={() => setEditingSource({...editingSource, icon: emoji})}
                      className={`w-10 h-10 text-xl rounded-lg border-2 transition-all ${
                        editingSource.icon === emoji 
                          ? 'border-teal-500 bg-teal-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  checked={editingSource.active}
                  onCheckedChange={(checked) => setEditingSource({...editingSource, active: checked})}
                />
                <Label>Aktiv</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingSource(null)}>
              Abbrechen
            </Button>
            <Button onClick={handleUpdateSource} disabled={saving}>
              {saving ? 'Speichert...' : 'Speichern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReferralSourcesSettingsPage;
